// firebaseClient.ts — Firebase Auth client singleton.
//
// Reads config from build-time Vite env vars (VITE_FIREBASE_*). Firebase
// Hosting also auto-serves the same config at /__/firebase/init.json, but
// we prefer env vars so local `vite dev` works standalone.
//
// Note: these values are PUBLIC and safe to ship in the client bundle.
// Firebase enforces security via Auth domain restrictions, Firestore rules,
// and the ADMIN_EMAILS allowlist on the Functions side.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  type Auth,
  type User,
} from "firebase/auth";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
};

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

export function isFirebaseConfigured(): boolean {
  return Boolean(config.apiKey && config.authDomain && config.projectId);
}

export function getFirebaseAuth(): Auth {
  if (!isFirebaseConfigured()) {
    throw new Error(
      "Firebase config missing: set VITE_FIREBASE_API_KEY, _AUTH_DOMAIN, _PROJECT_ID."
    );
  }
  if (!app) {
    app = initializeApp({
      apiKey: config.apiKey!,
      authDomain: config.authDomain!,
      projectId: config.projectId!,
    });
    auth = getAuth(app);
  }
  return auth!;
}

export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(getFirebaseAuth(), provider);
  return result.user;
}

export function signOut(): Promise<void> {
  return fbSignOut(getFirebaseAuth());
}
